import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Textarea } from "@foodpilot/foods";
import { FormPropertyProps } from "../../FormProperty";
import { LONG_TEXT_MAX_LENGTH } from "src/constants";
import { usePropertyContext } from "../../context/usePropertyContext";
import { FieldValueLayout } from "../FieldValueLayout";

export const LongText = (props: FormPropertyProps) => {
    const { property, position, showSubscore, dimensionValue } = props;

    const { post, onUpdate, getValidationError, canEdit, getElement } = usePropertyContext();

    const element = getElement(property.id, position);

    const _defaultValue = property.defaultValue ?? "";
    const initialValue = element?.value ?? "";

    const [previousValue, setPreviousValue] = useState(initialValue);
    const [value, setValue] = useState(initialValue);
    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);

    const genericValue = property.genericValue;

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    if (!post) return <CircularProgress />;

    const updatePropertyValue = (newValue?: string, isGenericValue?: boolean) => {
        const sanitizedValue = newValue?.trim() ?? "";

        onUpdate({
            postId: post.id,
            propertyId: Number(property.id),
            value: sanitizedValue,
            position: Number(props.position ?? 0),
            isGenericValue: isGenericValue,
        });
    };

    const isDefaultActivated = !property.readonly && _defaultValue && !genericValueSelected;
    const defaultValue = isDefaultActivated ? _defaultValue : undefined;

    const onCalculate = (newValue: string) => {
        if (newValue === previousValue) return;
        updatePropertyValue(newValue, undefined);
    };

    const validationState = getValidationError(property.id, position ?? 0);
    const isErrorState = validationState.hasValidationMessages && validationState.validationSeverity !== "warning";
    const errorMessage = isErrorState ? validationState.validationMessages[0]?.message : undefined;

    const isDisabled = property.readonly || !canEdit || genericValueSelected;
    const displayValue = value !== "_empty_" ? value : "";

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                defaultValue: defaultValue ?? null,
                genericValue: genericValue ?? null,
                isGenericValueSelected: genericValueSelected,
                dimensionValue: dimensionValue,
                forceHideScore: showSubscore === false,
                hideUnit: true,
                setDefaultValue: () => {
                    if (defaultValue) {
                        onCalculate?.(defaultValue);
                    }
                },
                setGenericValue: () => {
                    const v = !genericValueSelected ? genericValue : "";
                    setGenericValueSelected((prevState) => !prevState);
                    setValue(v ?? "");
                    updatePropertyValue(v, !genericValueSelected);
                },
            }}
        >
            <Textarea
                value={displayValue}
                onChange={(newValue) => setValue(newValue)}
                onFocus={() => setPreviousValue(value)}
                onBlur={(e) => {
                    onCalculate(e.currentTarget.value);
                }}
                disabled={isDisabled}
                error={isErrorState}
                errorMessage={errorMessage}
                maxLength={LONG_TEXT_MAX_LENGTH}
                minRows={4}
            />
        </FieldValueLayout>
    );
};
